import { IPluginRefObject } from 'gatsby';
import StoryblokClient, { StoryblokResult } from 'storyblok-js-client';
import config from '../../../gatsby-config';

interface StoryblokDatasourceResult extends StoryblokResult {
  slug: string,
  params: Record<string, unknown>;
  page: number
}

export interface StoryblokDatasourceEntry {
  id: number;
  name: string;
  value: string;
  dimension_value: string | null;
}

const getUrlParams = (): Record<string, string | true> => window.location.search.substr(1)
  .split('&')
  .filter((slug) => !!slug)
  .reduce((accumulator, slug) => ({
    ...accumulator,
    ...(slug.split('=')[0] && {
      [slug.split('=')[0]]: slug.split('=')[1] || true,
    }),
  }), {});

export const StoryblokService = {
  isEditorMode: () => global.window?.location.pathname === '/editor',

  getVersion: () => (StoryblokService.isEditorMode() ? 'draft' : 'published'),

  getConfig(): IPluginRefObject {
    return (config.plugins as IPluginRefObject[])
      .find((item) => item.resolve === 'gatsby-source-graphql') || {} as IPluginRefObject;
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getObject(): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { storyblok } = window as any;
    return storyblok || undefined;
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async redirect(callback?: (...args: any[]) => void): Promise<void> {
    return new Promise((resolve) => {
      const storyblok = StoryblokService.getObject();
      const params = getUrlParams();
      const isValidRedirect = typeof params.redirect === 'string';

      if (storyblok) {
        storyblok.get({ slug: isValidRedirect ? params.redirect as string : '/home' }, (...args) => {
          if (callback) {
            callback(...args);
          }
          resolve();
        });
      } else {
        window.location.href = isValidRedirect ? params.redirect as string : '/';
        resolve();
      }
    });
  },

  async getDatasources(
    client: StoryblokClient,
    slugs: string[],
    params?: Record<string, string | number>,
  ): Promise<StoryblokResult[]> {
    const initialResponses = await Promise.all(
      slugs.map((slug) => this.createDatasourceEntriesRequest(client, slug, 1, params)),
    );

    const additionalRequests = [];
    initialResponses.forEach((datasource) => {
      if (datasource.total <= datasource.perPage) {
        return;
      }
      const totalPages = datasource.total / datasource.perPage;

      for (let page = 1; page < totalPages; page += 1) {
        additionalRequests
          .push(this.createDatasourceEntriesRequest(client, datasource.slug, page + 1, params));
      }
    });

    if (additionalRequests.length === 0) {
      return this.reduceDatasourceResponses(initialResponses);
    }

    const additionalResponses = await Promise.all(additionalRequests);

    return this.reduceDatasourceResponses([...initialResponses, ...additionalResponses]);
  },

  createDatasourceEntriesRequest(
    client: StoryblokClient,
    slug: string,
    page: number,
    params?: Record<string, string | number>,
  ): Promise<StoryblokDatasourceResult> {
    return client.get('cdn/datasource_entries', {
      datasource: slug,
      ...params,
      per_page: 100,
      page,
      cv: +new Date(),
    }).then((response) => ({
      ...response,
      slug,
      params,
      page,
    }));
  },

  reduceDatasourceResponses(
    datasources: StoryblokDatasourceResult[],
  ): Record<string, StoryblokDatasourceEntry[]> {
    return datasources.reduce((previous, current) => ({
      ...previous,
      [current.slug]: [
        ...(previous[current.slug] || []),
        ...current.data.datasource_entries,
      ],
    }), {});
  },
};
